import { JobType } from "@1games-ru/region-shared/dist/enums/business/service";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { serviceInfoSelector, setServiceInfo } from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useServiceHandlers() {
  const { t, i18n } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [isPending, setIsPending] = useState(false);

  const serviceInfo = useAppSelector(serviceInfoSelector);

  const updateServiceInfo = useCallback(async () => {
    if (serviceInfo) {
      try {
        const response = await BusinessMpProvider.getServiceState(serviceInfo.businessId);

        if (response instanceof RpcMpProviderFailedResponse) {
          if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.serviceGetInfoError"));
          }
          return;
        }

        dispatch(setServiceInfo(response.data));
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.serviceGetInfoError"));
      }
    }
  }, [dispatch, serviceInfo, t, i18n]);

  const startBusinessJob = useCallback(
    async (businessId: number, jobType: JobType, isImmediately: boolean) => {
      setIsPending(true);
      try {
        const response = await BusinessMpProvider.startBusinessJob(
          businessId,
          jobType,
          isImmediately,
        );

        if (response instanceof RpcMpProviderFailedResponse) {
          if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
            return;
          }

          console.error(response.error.message);
          if (jobType === JobType.Cleaning) {
            if (isImmediately) {
              toast.error(t("business.toast.serviceNotCallUpCleaning"));
            } else {
              toast.error(t("business.toast.serviceCleaningNotDone"));
            }
          }
          if (jobType === JobType.Repairing) {
            if (isImmediately) {
              toast.error(t("business.toast.serviceNotCallUpMaster"));
            } else {
              toast.error(t("business.toast.serviceRepairNotCompleted"));
            }
          }
          return;
        }

        if (jobType === JobType.Cleaning) {
          if (isImmediately) {
            toast.success(t("business.toast.serviceCallUpCleaning"));
            updateServiceInfo();
          } else {
            toast.success(t("business.toast.serviceCleaningDone"));
          }
        }
        if (jobType === JobType.Repairing) {
          if (isImmediately) {
            toast.success(t("business.toast.serviceCallUpMaster"));
            updateServiceInfo();
          } else {
            toast.success(t("business.toast.serviceRepairCompleted"));
          }
        }
      } catch (e) {
        console.error(e);
        if (jobType === JobType.Cleaning) {
          if (isImmediately) {
            toast.error(t("business.toast.serviceNotCallUpCleaning"));
          } else {
            toast.error(t("business.toast.serviceCleaningNotDone"));
          }
        }
        if (jobType === JobType.Repairing) {
          if (isImmediately) {
            toast.error(t("business.toast.serviceNotCallUpMaster"));
          } else {
            toast.error(t("business.toast.serviceRepairNotCompleted"));
          }
        }
      } finally {
        setIsPending(false);
      }
    },
    [t, i18n, updateServiceInfo],
  );

  return { isPending, startBusinessJob };
}
